import { useState } from "react";
import { Search, Download, CalendarIcon, X, FileSpreadsheet, FileText, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ExcelJS from "exceljs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Calendar } from "@/components/ui/calendar";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface TableToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onExportCSV: () => void;
  onExportExcel?: () => void;
  placeholder?: string;
  fechaSubidaRange?: DateRange;
  onFechaSubidaChange?: (range: DateRange) => void;
  fechaRange?: DateRange;
  onFechaChange?: (range: DateRange) => void;
  estadoFilter?: string;
  onEstadoChange?: (value: string) => void;
  showFilters?: boolean;
}

interface Preset {
  label: string;
  getRange: () => DateRange;
}

const PRESETS: Preset[] = [
  {
    label: "Hoy",
    getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }),
  },
  {
    label: "Últimos 7 días",
    getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    label: "Últimos 30 días",
    getRange: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }),
  },
  {
    label: "Este Mes",
    getRange: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }),
  },
  {
    label: "Mes Anterior",
    getRange: () => {
      const prev = subMonths(new Date(), 1);
      return { from: startOfMonth(prev), to: endOfMonth(prev) };
    },
  },
];

function formatRangeLabel(range: DateRange, placeholder: string): string {
  if (!range.from && !range.to) return placeholder;
  const from = range.from ? format(range.from, "dd MMM, yyyy", { locale: es }) : "…";
  const to = range.to ? format(range.to, "dd MMM, yyyy", { locale: es }) : "…";
  return `${from} - ${to}`;
}

function DateRangeCalendarContent({ label, range, onChange, onClose, hasValue }: {
  label: string; range: DateRange; onChange: (r: DateRange) => void; onClose: () => void; hasValue: boolean;
}) {
  const isMobile = useIsMobile();

  const applyPreset = (preset: Preset) => {
    onChange(preset.getRange());
    onClose();
  };

  return (
    <div className={cn("flex", isMobile && "flex-col")}>
      {/* Presets */}
      <div className={cn(
        "flex gap-1 p-3",
        isMobile ? "flex-row flex-wrap border-b border-border" : "flex-col border-r border-border min-w-[140px]"
      )}>
        <p className={cn("text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1", isMobile && "w-full")}>
          Accesos rápidos
        </p>
        {PRESETS.map((preset) => (
          <Button
            key={preset.label}
            variant="ghost"
            size="sm"
            className="justify-start text-xs h-7 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </Button>
        ))}
        {hasValue && (
          <>
            <Separator className={cn(isMobile ? "hidden" : "my-1")} />
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-xs h-7 px-2 text-destructive hover:text-destructive"
              onClick={() => { onChange({}); onClose(); }}
            >
              <X className="h-3 w-3 mr-1" /> Limpiar
            </Button>
          </>
        )}
      </div>

      {/* Calendar */}
      <div className="p-3">
        <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
        <Calendar
          mode="range"
          selected={range.from ? { from: range.from, to: range.to } : undefined}
          onSelect={(r) => {
            onChange({ from: r?.from, to: r?.to });
            if (r?.from && r?.to) onClose();
          }}
          numberOfMonths={isMobile ? 1 : 2}
          className={cn("p-0 pointer-events-auto")}
          locale={es}
        />
        {hasValue && (
          <p className="text-[10px] text-muted-foreground text-center mt-2">
            {formatRangeLabel(range, "")}
          </p>
        )}
      </div>
    </div>
  );
}

function DateRangeFilter({ label, range, onChange }: { label: string; range: DateRange; onChange: (r: DateRange) => void }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const hasValue = !!(range.from || range.to);

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className={cn(
        "gap-1.5 border-border text-xs h-8 max-w-[260px] justify-start",
        hasValue ? "text-primary border-primary/50" : "text-muted-foreground"
      )}
    >
      <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">
        {formatRangeLabel(range, label)}
      </span>
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>{label}</DrawerTitle>
            </DrawerHeader>
            <div className="px-2 pb-4">
              <DateRangeCalendarContent
                label={label}
                range={range}
                onChange={onChange}
                onClose={() => setOpen(false)}
                hasValue={hasValue}
              />
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DateRangeCalendarContent
          label={label}
          range={range}
          onChange={onChange}
          onClose={() => setOpen(false)}
          hasValue={hasValue}
        />
      </PopoverContent>
    </Popover>
  );
}

const ESTADOS = ["Pendiente", "Procesando", "Pagado", "Completado", "Cancelado"];

export function TableToolbar({
  searchValue, onSearchChange, onExportCSV, onExportExcel,
  placeholder = "Buscar por No. Documento o Emisor...",
  fechaSubidaRange, onFechaSubidaChange,
  fechaRange, onFechaChange,
  estadoFilter, onEstadoChange,
  showFilters = true,
}: TableToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border">
      <div className="relative flex-1 min-w-[180px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9 bg-muted/30 border-border text-sm h-8"
        />
      </div>

      {showFilters && onFechaSubidaChange && fechaSubidaRange && (
        <DateRangeFilter label="Fecha Subida" range={fechaSubidaRange} onChange={onFechaSubidaChange} />
      )}

      {showFilters && onFechaChange && fechaRange && (
        <DateRangeFilter label="Fecha Documento" range={fechaRange} onChange={onFechaChange} />
      )}

      {showFilters && onEstadoChange && (
        <Select value={estadoFilter || "all"} onValueChange={(v) => onEstadoChange(v === "all" ? "" : v)}>
          <SelectTrigger className={cn(
            "h-8 w-[140px] text-xs border-border",
            estadoFilter ? "text-primary border-primary/50" : "text-muted-foreground"
          )}>
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => (
              <SelectItem key={e} value={e}>{e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5 border-border text-muted-foreground hover:text-primary hover:border-primary/50 h-8 text-xs">
            <Download className="h-3.5 w-3.5" />
            Exportar Datos
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onExportCSV} className="gap-2 text-xs cursor-pointer">
            <FileText className="h-4 w-4" />
            📄 Descargar como CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onExportExcel} className="gap-2 text-xs cursor-pointer">
            <FileSpreadsheet className="h-4 w-4" />
            📊 Descargar como Excel (.xlsx)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function exportToCSV(headers: string[], rows: string[][], filename: string) {
  const csvContent = [
    headers.join(","),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportToExcel(headers: string[], rows: (string | number)[][], filename: string, numericColumns?: number[]) {
  const numSet = new Set(numericColumns ?? []);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Datos");

  // Header row
  const headerRow = ws.addRow(headers);
  headerRow.eachCell(cell => {
    cell.font = { bold: true };
  });

  // Data rows — sanitize null/undefined
  for (const row of rows) {
    const values = row.map((v, i) => {
      if (v == null || v === "") {
        return numSet.has(i) ? 0 : "";
      }
      if (numSet.has(i)) {
        const num = Number(v);
        return isNaN(num) ? String(v) : num;
      }
      return String(v);
    });
    ws.addRow(values);
  }

  // Auto-fit column widths
  ws.columns.forEach((col, i) => {
    let max = headers[i]?.length ?? 10;
    for (const row of rows) {
      const len = String(row[i] ?? "").length;
      if (len > max) max = len;
    }
    col.width = Math.min(max + 2, 40);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
